import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";
import {Tooltip} from "primereact/tooltip";

import {CampaignContext} from "../../App";
import {SellerWorkspaceService} from "../../service/SellerWorkspaceService";
import {formatCurrencyText, getProductDetailLink, isAdmin} from "../../utils/Utils";
import SellerIdentityChips from "./SellerIdentityChips";
import SellerLinkEditor from "./SellerLinkEditor";
import {officialLabel, sellerChips} from "./sellerIdentity";
import {formatCount, formatGap} from "./sellerFormat";

const sellerWorkspaceService = new SellerWorkspaceService();
const CURRENCY = 'TRY';
const STEP_MATCH = 'seller_store_match';
// A request naming one store ignores its scope; the API still wants a valid one.
const SCOPE_ONE_SELLER = 'demo';
// PrimeReact shows an option whose value is null as an empty box.
const ALL_CAMPAIGNS = 'all';
const COMPARED = [{key: 'trendyol', label: 'Trendyol'}, {key: 'hepsiburada', label: 'Hepsiburada'}];
const sectionTitle = {fontWeight: 700, margin: '18px 0 8px'};
const muted = {color: '#6b7280'};

const StoreLink = ({t, url}) => url
    ? <a href={url} target="_blank" rel="noopener noreferrer">{t('onboarding.sites.identity.openStore', 'Mağazayı aç')}</a>
    : '—';

const matchText = (status, t) => {
    if (status === 'confirmed') return `✓ ${t('onboarding.sites.identity.confirmed', 'onaylandı')}`;
    if (status === 'auto') return t('onboarding.sites.identity.automatic', 'otomatik');
    return t('sellers_workspace.detail.noMatch', 'eşleşme yok');
};

const OtherPrice = ({t, product, ownKey, marketplaceKey}) => {
    const own = product.prices?.[ownKey]?.value;
    const other = product.prices?.[marketplaceKey]?.value;
    if (!other) return '—';
    const gap = own ? (other - own) / own * 100 : null;
    return <span>
        {formatCurrencyText(other, CURRENCY)}
        {gap !== null && <small style={{...muted, marginLeft: 4}}>({formatGap(gap, t)})</small>}
    </span>;
};

const Identity = ({t, store, sameCompany}) => {
    const lines = [
        store.legal_title && [t('sellers_workspace.detail.legalTitle', 'Ticaret ünvanı'), store.legal_title],
        store.tax_id && [t('sellers_workspace.detail.taxId', 'VKN'), store.tax_id],
        store.city && [t('sellers_workspace.detail.city', 'Şehir'), store.city],
    ].filter(Boolean);
    return <div>
        <div style={sectionTitle}>{t('sellers_workspace.detail.identity', 'Resmi kayıt')}</div>
        {lines.length === 0 && <span style={muted}>{t('sellers_workspace.detail.identityUnread', 'Henüz okunmadı.')}</span>}
        {lines.map(([label, value]) => <div key={label}><span style={muted}>{label}:</span> {value}</div>)}
        {sameCompany?.length > 0 &&
            <div>
                <span style={muted}>{t('sellers_workspace.detail.sameCompany', 'Aynı şirket')}:</span> {sameCompany.join(', ')}
                <small style={{...muted, marginLeft: 6}}>{t('sellers_workspace.detail.notSummed', 'Toplamlar birleştirilmez.')}</small>
            </div>}
    </div>;
};

// Level 3: one seller on each marketplace. Numbers under "izlenen" come from tracked products only:
// a campaign's rows are a small sample of a store, not its catalogue.
const SellerDetail = ({t, toast, onError, storeId}) => {
    const {campaigns} = useContext(CampaignContext);
    const [answer, setAnswer] = useState(null);
    const [failed, setFailed] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [campaignId, setCampaignId] = useState(null);
    const [onlyElsewhere, setOnlyElsewhere] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [searching, setSearching] = useState(false);

    const load = useCallback(() => sellerWorkspaceService.getSeller(storeId, campaignId)
        .then(found => { setAnswer(found); setFailed(false); setNotFound(false); })
        .catch(error => { error?.response?.status === 404 ? setNotFound(true) : setFailed(true); }), [storeId, campaignId]);
    useEffect(() => { load(); }, [load]);

    const search = () => {
        setSearching(true);
        sellerWorkspaceService.runStep(STEP_MATCH, SCOPE_ONE_SELLER, storeId)
            .then(() => toast.current?.show({severity: 'success', summary: t('sellers_workspace.detail.search', 'Bu satıcıyı ara'),
                                             detail: t('sellers_workspace.detail.searchStarted', 'Arama başladı. Sonuç birkaç dakika içinde burada görünür.')}))
            .catch(onError).finally(() => setSearching(false));
    };

    if (notFound) return <Message severity="warn" text={t('sellers_workspace.detail.notFound', 'Bu satıcı bulunamadı.')}/>;
    if (failed && !answer) {
        return <div>
            <Message severity="warn" style={{display: 'block', marginBottom: 10}}
                     text={t('sellers_workspace.loadFailed', 'Satıcı bilgileri yüklenemedi.')}/>
            <Button label={t('sellers_workspace.retry', 'Tekrar dene')} className="p-button-outlined p-button-sm" onClick={load}/>
        </div>;
    }
    if (!answer) return null;

    const {store, view, marketplaces} = answer;
    const ownKey = store.marketplace_key;
    const chipMarketplaces = marketplaces.map(({key, label}) => ({key, label}));
    const editedChip = editingKey ? sellerChips(view, chipMarketplaces).find(chip => chip.key === editingKey) : null;
    const products = onlyElsewhere ? answer.products.filter(product => !product.prices?.[ownKey]) : answer.products;
    const rows = [{key: ownKey, label: t('sellers_workspace.detail.own', 'Koçtaş'), own: true, store_url: store.store_url, total: store.product_count},
                  ...marketplaces];

    return <div>
        <h5 style={{marginTop: 0}}>{store.name}</h5>
        <Tooltip target=".seller-identity-chip" position="top"/>
        <SellerIdentityChips t={t} view={view} marketplaces={chipMarketplaces} notFoundOn={store.not_found_on} onChipClick={(chip) => setEditingKey(chip.key)}/>
        {isAdmin() &&
            <div style={{marginTop: 10}}>
                <Button label={t('sellers_workspace.detail.search', 'Bu satıcıyı ara')} icon="pi pi-search" className="p-button-outlined p-button-sm" loading={searching}
                        onClick={search}/>
            </div>}

        <div style={sectionTitle}>{t('sellers_workspace.detail.marketplaces', 'Pazaryerleri')}</div>
        <DataTable value={rows} dataKey="key" responsiveLayout="scroll">
            <Column header={t('sellers_workspace.summary.marketplace', 'Pazaryeri')} field="label"/>
            <Column header={t('sellers_workspace.detail.match', 'Eşleşme')} body={(row) => row.own ? '—' : (row.not_found ? t('sellers_workspace.notFound', 'bulunamadı')
                        : [matchText(row.status, t), officialLabel(row.official, t)].filter(Boolean).join(' · '))}/>
            <Column header={t('sellers_workspace.detail.store', 'Mağaza')} body={(row) => <StoreLink t={t} url={row.store_url}/>}/>
            <Column header={t('sellers_workspace.detail.total', 'Toplam ürün')} body={(row) => formatCount(row.total, t)}/>
            <Column header={t('sellers_workspace.detail.shared', 'İzlenen ortak ürün')} body={(row) => (row.own || row.shared_products === null || row.shared_products === undefined) ? '—' : formatCount(row.shared_products, t)}/>
            <Column header={t('sellers_workspace.detail.gap', 'Medyan fiyat farkı')} body={(row) => row.own ? '—' : formatGap(row.median_gap, t)}/>
            <Column header={t('sellers_workspace.detail.dispatch', 'Kargoya verme')}
                    body={(row) => (row.dispatch_days === null || row.dispatch_days === undefined)
                        ? '—' : t('sellers_workspace.detail.days', '{{count}} gün', {count: row.dispatch_days})}/>
        </DataTable>

        <Identity t={t} store={store} sameCompany={answer.same_company}/>

        <div style={sectionTitle}>{t('sellers_workspace.detail.products', 'İzlenen ürünler')}</div>
        <div style={{display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10}}>
            <Dropdown value={campaignId || ALL_CAMPAIGNS} style={{width: '16em'}}
                      options={[{label: t('sellers_workspace.detail.allCampaigns', 'Tüm kampanyalar'), value: ALL_CAMPAIGNS},
                                ...(campaigns || []).map(campaign => ({label: campaign.name, value: campaign.id}))]}
                      onChange={(e) => setCampaignId(e.value === ALL_CAMPAIGNS ? null : e.value)}/>
            <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
                <Checkbox inputId="seller-only-elsewhere" checked={onlyElsewhere} onChange={(e) => setOnlyElsewhere(e.checked)}/>
                <label htmlFor="seller-only-elsewhere">{t('sellers_workspace.detail.onlyElsewhere', "Koçtaş'ta satmadıkları")}</label>
            </span>
        </div>
        <DataTable value={products} dataKey="product_id" responsiveLayout="scroll" paginator={products.length > 20} rows={20}
                   emptyMessage={t('sellers_workspace.detail.noProducts', 'Bu satıcının izlenen ürünü yok.')}>
            <Column header={t('sellers_workspace.detail.product', 'Ürün')} body={(product) => getProductDetailLink(product.product_id, product.name)}/>
            <Column header={t('sellers_workspace.detail.own', 'Koçtaş')}
                    body={(product) => product.prices?.[ownKey]?.value ? formatCurrencyText(product.prices[ownKey].value, CURRENCY) : '—'}/>
            {COMPARED.map(({key, label}) =>
                <Column key={key} header={label} body={(product) => <OtherPrice t={t} product={product} ownKey={ownKey} marketplaceKey={key}/>}/>)}
        </DataTable>
        <p style={{...muted, marginTop: 10}}>
            {t('sellers_workspace.detail.note', 'Ortak ürün, fiyat farkı ve bu tablo yalnız izlenen ürünlerde hesaplanır; mağazanın tüm kataloğu değildir.')}
        </p>
        {editedChip &&
            <SellerLinkEditor t={t} toast={toast} onError={onError} chip={editedChip} onChanged={load}
                              seller={{marketplace_key: view.marketplace_key, seller_name: view.seller_name}}
                              onClose={() => setEditingKey(null)}/>}
    </div>;
};

export default SellerDetail;
